import type { MatchEvent } from "../../../../store/useMatchLogStore";
import { zoneValue, AttackDir } from "./xtGrid";
import { sameTeamId } from "../../utils/teamId";
import type {
  MomentumOptions,
  MomentumPoint,
  MomentumResult,
  GoalMarker,
  CardMarker,
  MomentumBar,
} from "./momentumTypes";

// Danger-weighted fallback values for events without usable coordinates.
const PROXY: Record<string, number> = {
  Shot: 0.08,
  Header: 0.05,
  Recovery: 0.02,
  Interception: 0.02,
  SetPiece: 0.03,
  Carry: 0.015,
  Pass: 0.01,
};

function absoluteMinute(ev: MatchEvent): number {
  const mm = parseInt((ev.match_clock || "0").split(":")[0], 10);
  return Number.isNaN(mm) ? 0 : mm;
}

function isGoal(ev: MatchEvent): boolean {
  return (
    ev.type === "Shot" &&
    (ev.data?.outcome === "Goal" || ev.data?.outcome === "goal")
  );
}

// PMA-COCK-192: a straight Red or a Yellow (Second) is a dismissal, drawn on
// the offending team's side of the axis. A plain Yellow draws nothing. Same
// case-insensitive tolerance isGoal applies to Goal/goal above.
function cardMarkerKind(ev: MatchEvent): CardMarker["kind"] | null {
  if (ev.type !== "Card") return null;
  const raw = String(ev.data?.card_type ?? "").toLowerCase();
  if (raw === "red") return "red";
  if (raw === "yellow (second)") return "secondYellow";
  return null;
}

// Per-event threat: xT delta when coordinates exist, else a type-based proxy.
function eventThreat(
  ev: MatchEvent,
  dir: AttackDir,
  cap: number,
): { value: number; fallback: boolean } {
  const start = ev.location;
  const end = ev.data?.end_location as [number, number] | undefined;
  const hasCoords =
    Array.isArray(start) &&
    start.length === 2 &&
    Array.isArray(end) &&
    end.length === 2;
  if ((ev.type === "Pass" || ev.type === "Carry") && hasCoords) {
    const delta = zoneValue(end, dir) - zoneValue(start, dir);
    return { value: Math.min(Math.max(delta, 0), cap), fallback: false };
  }
  const proxy = PROXY[ev.type] ?? 0;
  return { value: Math.min(proxy, cap), fallback: proxy > 0 };
}

export function computeMomentum(
  events: MatchEvent[],
  opts: MomentumOptions,
): MomentumResult {
  const { homeTeamId, awayTeamId } = opts;
  const decay = opts.decay ?? 0.6;
  const windowMinutes = opts.windowMinutes ?? 5;
  const cap = opts.xtCap ?? 0.1;

  if (!events.length)
    return {
      points: [],
      bars: [],
      goals: [],
      cards: [],
      goalPeriods: [],
      cardPeriods: [],
      usedFallback: false,
    };

  // maxThreat[side][minute] = max per-minute threat
  const maxThreat: Record<"home" | "away", Map<number, number>> = {
    home: new Map(),
    away: new Map(),
  };
  const minutePeriod = new Map<number, number>();
  // EPIC-110: per-period threat maps feeding `bars` (same maths as `points`).
  const periodThreat = new Map<
    number,
    {
      home: Map<number, number>;
      away: Map<number, number>;
      min: number;
      max: number;
    }
  >();
  const goals: GoalMarker[] = [];
  const cards: CardMarker[] = [];
  const goalPeriods: number[] = [];
  const cardPeriods: number[] = [];
  let usedFallback = false;
  let maxMinute = 0;

  for (const ev of events) {
    const side: "home" | "away" | null = sameTeamId(ev.team_id, homeTeamId)
      ? "home"
      : sameTeamId(ev.team_id, awayTeamId)
        ? "away"
        : null;
    if (!side) continue;
    const minute = absoluteMinute(ev);
    if (minute > maxMinute) maxMinute = minute;
    minutePeriod.set(minute, ev.period);
    const period = typeof ev.period === "number" ? ev.period : 1;
    let bucket = periodThreat.get(period);
    if (!bucket) {
      bucket = { home: new Map(), away: new Map(), min: minute, max: minute };
      periodThreat.set(period, bucket);
    }
    if (minute < bucket.min) bucket.min = minute;
    if (minute > bucket.max) bucket.max = minute;
    if (isGoal(ev)) {
      goals.push({ minute, side });
      goalPeriods.push(period);
    }
    const cardKind = cardMarkerKind(ev);
    if (cardKind) {
      cards.push({ minute, side, kind: cardKind });
      cardPeriods.push(period);
    }

    const { value, fallback } = eventThreat(ev, side, cap);
    if (fallback && value > 0) usedFallback = true;
    if (value <= 0) continue;
    const cur = maxThreat[side].get(minute) ?? 0;
    if (value > cur) maxThreat[side].set(minute, value);
    const curP = bucket[side].get(minute) ?? 0;
    if (value > curP) bucket[side].set(minute, value);
  }

  const smooth = (series: Map<number, number>, t: number): number => {
    let sum = 0;
    for (let m = Math.max(0, t - windowMinutes); m <= t; m++) {
      const v = series.get(m);
      if (v) sum += Math.exp(-decay * (t - m)) * v;
    }
    return sum;
  };

  const bars: MomentumBar[] = [];
  for (const period of [...periodThreat.keys()].sort((a, b) => a - b)) {
    const b = periodThreat.get(period)!;
    for (let t = b.min; t <= b.max; t++) {
      const home = smooth(b.home, t);
      const away = smooth(b.away, t);
      bars.push({ period, minute: t, home, away, momentum: home - away });
    }
  }

  const points: MomentumPoint[] = [];
  for (let t = 0; t <= maxMinute; t++) {
    const weighted = (side: "home" | "away"): number =>
      smooth(maxThreat[side], t);
    const home = weighted("home");
    const away = weighted("away");
    if (home === 0 && away === 0 && !minutePeriod.has(t)) continue;
    points.push({
      minute: t,
      period: minutePeriod.get(t) ?? (t < 45 ? 1 : 2),
      home,
      away,
      momentum: home - away,
    });
  }

  return {
    points,
    bars,
    goals,
    cards,
    goalPeriods,
    cardPeriods,
    usedFallback,
  };
}
